<script setup lang="ts">
import BaseLayout from "@/layouts/BaseLayout.vue";
import {computed, ref} from "vue";
import {useAuth} from "@/composables/auth";
import {useColors} from "@/composables/colors";
import {useDate} from "@/composables/date";
import {router} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import {AssessmentSummary, Pagination} from "@/types";
import {DataTableHeader} from "vuetify/framework";
import IconTooltip from "@/components/tooltips/IconTooltip.vue";
import CompletedChip from "@/components/chips/CompletedChip.vue";
import ClosedChip from "@/components/chips/ClosedChip.vue";
import OpenChip from "@/components/chips/OpenChip.vue";
import IconDateTooltip from "@/components/tooltips/IconDateTooltip.vue";
import DueDateTooltip from "@/components/tooltips/DueDateTooltip.vue";
import {useNumber} from "@/composables/number";
import TitleAvatar from "@/components/avatars/TitleAvatar.vue";

defineOptions({layout: BaseLayout})

const {user} = useAuth()
const {randomColor} = useColors()
const {shortDate} = useDate()
const {percentage} = useNumber()

interface FavoriteCapacityFramework {
    id: number
    name: string
    updated_at: string
}

interface Props {
    title: string
    capacityFrameworks: Array<FavoriteCapacityFramework>
    assessments: Pagination<AssessmentSummary>
}

const props = defineProps<Props>();

const isFavoriteCapacityFrameworksShown = ref<boolean>(true)
const isRecentAssessmentsShown = ref<boolean>(true)
const today = computed(() => {
    const options: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
    };
    return new Date().toLocaleDateString(undefined, options);
})

const headers: Array<DataTableHeader> = [
    {
        title: 'Title',
        value: 'title',
        sortable: false,
        align: 'start',
        width: '70%'
    },
    {
        title: 'Completion',
        value: 'completion',
        sortable: false,
        align: 'start',
        width: '30%'
    },
]

const goToAssessment = (id: number) => {
    router.get(route('assessments.show', {assessment: id}), {});
};
</script>

<template>
    <v-container>
        <v-sheet class="text-center">
            <p class="my-3 text-medium-emphasis font-weight-regular">
                {{ today }}
            </p>
            <h1 class="text-h4 font-weight-medium">
                Welcome back, {{ user.name }}!
            </h1>
            <div class="text-start mt-15">
                <p class="text-h6 font-weight-bold">
                    <v-icon-btn
                        :icon="isFavoriteCapacityFrameworksShown ? 'mdi-chevron-down' : 'mdi-chevron-right'"
                        size="lg"
                        rounded="lg"
                        class="mr-2"
                        variant="text"
                        @click="isFavoriteCapacityFrameworksShown = !isFavoriteCapacityFrameworksShown"
                    />
                    Favorite capacity frameworks
                    <v-btn
                        variant="text"
                        color="base"
                        class="float-end"
                        @click="router.get(route('capacity-frameworks.index'))"
                    >
                        View all
                    </v-btn>
                </p>
                <v-divider class="my-3" />
                <v-expand-transition>
                    <v-row
                        v-if="isFavoriteCapacityFrameworksShown"
                        class="d-flex flex-row"
                    >
                        <v-col
                            v-if="props.capacityFrameworks.length === 0"
                            cols="auto"
                        >
                            <v-sheet
                                max-width="128"
                                class="mx-3 mb-2"
                            >
                                <v-card
                                    variant="flat"
                                    width="128"
                                    height="128"
                                    rounded="xl"
                                    class="align-center justify-center border-sm border-dashed"
                                    @click="router.get(route('capacity-frameworks.index'))"
                                >
                                    <v-card-text class="font-weight-medium text-h3 align-self-center text-center mt-3">
                                        <v-icon
                                            size="48"
                                            color="grey-lighten-3"
                                        >
                                            mdi-plus
                                        </v-icon>
                                    </v-card-text>
                                </v-card>
                                <p class="text-center text-sm text-wrap mt-2 font-weight-black text-medium-emphasis">
                                    Add your favorite capacity frameworks
                                </p>
                            </v-sheet>
                        </v-col>
                        <v-col
                            v-for="capacityFramework in props.capacityFrameworks"
                            :key="capacityFramework.id"
                            cols="auto"
                        >
                            <v-sheet
                                max-width="128"
                                class="mx-3 mb-2"
                            >
                                <v-card
                                    variant="flat"
                                    width="128"
                                    height="128"
                                    rounded="xl"
                                    :color="randomColor(capacityFramework.id)"
                                    class="align-center justify-center"
                                    @click="router.get(route('capacity-frameworks.show', {capacityFramework: capacityFramework.id}))"
                                >
                                    <v-card-text class="font-weight-medium text-h3 align-self-center text-center mt-3">
                                        {{ capacityFramework.name.charAt(0).toUpperCase() }}
                                    </v-card-text>
                                </v-card>
                                <p class="text-center text-sm text-wrap mt-2 font-weight-black">
                                    {{ capacityFramework.name }}
                                </p>
                                <p class="text-center text-sm text-wrap mt-2 text-disabled">
                                    {{ shortDate(capacityFramework.updated_at) }}
                                </p>
                            </v-sheet>
                        </v-col>
                    </v-row>
                </v-expand-transition>
            </div>
            <div class="text-start mt-15">
                <p class="text-h6 font-weight-bold">
                    <v-icon-btn
                        :icon="isRecentAssessmentsShown ? 'mdi-chevron-down' : 'mdi-chevron-right'"
                        size="lg"
                        rounded="lg"
                        class="mr-2"
                        variant="text"
                        @click="isRecentAssessmentsShown = !isRecentAssessmentsShown"
                    />
                    Recent assessments
                    <v-btn
                        variant="text"
                        color="base"
                        class="float-end"
                        @click="router.get(route('assessments.index'))"
                    >
                        View all
                    </v-btn>
                </p>
                <v-divider class="my-3" />
                <v-expand-transition>
                    <v-data-table
                        v-if="isRecentAssessmentsShown"
                        :headers="headers"
                        :items="props.assessments.data"
                        :items-per-page="props.assessments.per_page"
                        :page="props.assessments.current_page"
                        disable-sort
                        hide-default-header
                        hide-default-footer
                        no-data-text="No results found."
                    >
                        <template #no-data>
                            <v-sheet class="mt-10">
                                <v-row
                                    class="fill-height"
                                    align="center"
                                    justify="center"
                                >
                                    <v-col
                                        cols="12"
                                        class="text-center"
                                    >
                                        <v-avatar size="128">
                                            <v-icon
                                                size="128"
                                                color="grey-lighten-2"
                                            >
                                                mdi-emoticon-sad-outline
                                            </v-icon>
                                        </v-avatar>
                                        <h2 class="text-h5 font-weight-bold">
                                            No assessments found.
                                        </h2>
                                    </v-col>
                                </v-row>
                            </v-sheet>
                        </template>
                        <template #[`item.title`]="{ item }">
                            <v-list-item>
                                <template #prepend>
                                    <title-avatar
                                        :id="item.id"
                                        :title="item.title"
                                        @click="goToAssessment(item.id)"
                                    />
                                </template>
                                <v-list-item-title
                                    class="text-body-2 font-weight-bold"
                                >
                                    <span
                                        class="custom-link"
                                        @click="goToAssessment(item.id)"
                                    >
                                        {{ item.title }}
                                    </span>
                                    <icon-tooltip
                                        v-if="!item.is_public"
                                        icon="mdi-lock-outline"
                                        class="ml-1"
                                        size="small"
                                    >
                                        Anonymized answers - The administrator cannot see the answers of the resource.
                                    </icon-tooltip>
                                </v-list-item-title>
                                <v-list-item-subtitle class="text-sm">
                                    <v-icon
                                        color="grey-darken-3"
                                        size="small"
                                    >
                                        mdi-bookmark-outline
                                    </v-icon>
                                    {{ item.capacity_framework_name }}
                                </v-list-item-subtitle>
                                <template #append>
                                    <due-date-tooltip
                                        v-if="item.due_date"
                                        :date="item.due_date"
                                        :completed="item.status === 'completed'"
                                    >
                                        {{ shortDate(item.due_date) }}
                                    </due-date-tooltip>
                                    <icon-date-tooltip
                                        icon="mdi-update"
                                        :date="item.updated_at"
                                    >
                                        Last updated
                                    </icon-date-tooltip>
                                    <open-chip v-if="item.status === 'open'" />
                                    <completed-chip v-else-if="item.status === 'completed'" />
                                    <closed-chip v-else-if="item.status === 'closed'" />
                                </template>
                            </v-list-item>
                        </template>
                        <template #[`item.completion`]="{ item }">
                            <div
                                v-if="item.capacity_indicators_count > 0"
                                class="d-flex flex-row align-center w-100"
                            >
                                <div class="text-grey-darken-3 text-sm mr-2 text-no-wrap">
                                    {{ item.answers_count }}/{{ item.capacity_indicators_count }} complete
                                </div>
                                <v-progress-linear
                                    :model-value="(item.answers_count / item.capacity_indicators_count) * 100"
                                    color="primary"
                                    class="flex-grow-1"
                                    height="6"
                                    rounded
                                    rounded-bar
                                />
                                <div
                                    class="text-grey-darken-3 text-sm ml-2"
                                >
                                    {{ percentage(item.answers_count / item.capacity_indicators_count) }}
                                </div>
                            </div>
                        </template>
                    </v-data-table>
                </v-expand-transition>
            </div>
        </v-sheet>
    </v-container>
</template>

<style scoped>
:deep(.v-list-item__prepend) {
    width: 48px;
}

:deep(.text-sm) {
    font-size: 0.75rem !important;
}
</style>
